import { BaseInput } from '@/components/Form/BaseInput';
import { ArrowRightIcon, CalendarIcon } from '@heroicons/react/16/solid';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { enUS } from 'date-fns/locale/en-US';
import { ja } from 'date-fns/locale/ja';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.min.css';
import { useTranslation } from 'react-i18next';

registerLocale('ja', ja);
registerLocale('en', enUS);

// react-datepickerの型定義でunion型を利用しており、generics などを利用して事前に型を絞り込むことができないため、
// 明示的に型を定義する必要がある。
type SingleDatePickerProps = {
  selected?: Date | null;
  onChange?: (
    date: Date | null,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void;
  locale?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
};

const ReactDatePickerBase = ({
  selected,
  onChange,
  locale,
  minDate,
  maxDate,
}: SingleDatePickerProps) => {
  const { t } = useTranslation();

  // カスタムヘッダー: 年と月の選択を分かりやすく表示
  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    changeYear,
    changeMonth,
  }: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
    changeYear: (year: number) => void;
    changeMonth: (month: number) => void;
  }) => {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();

    // 年選択の範囲を計算
    const startYear = minDate ? minDate.getFullYear() : currentYear - 10;
    const endYear = maxDate ? maxDate.getFullYear() : currentYear + 1;
    const years: number[] = [];
    for (let i = startYear; i <= endYear; i++) {
      years.push(i);
    }

    // i18nのロケール設定に基づいて月名を自動生成
    const months = Array.from({ length: 12 }, (_, i) => {
      const monthDate = new Date(2000, i, 1);
      return new Intl.DateTimeFormat(locale, { month: 'short' }).format(
        monthDate
      );
    });

    // i18nのロケール設定に基づいて年の表示を自動生成
    const formatYear = (year: number) => {
      const yearDate = new Date(year, 0, 1);
      return new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(
        yearDate
      );
    };

    return (
      <div className="flex items-center justify-between px-2 py-1">
        {/* 前月ボタン */}
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className="rounded p-0.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t('前の月')}
        >
          <ChevronLeftIcon className="size-3.5 text-gray-600" />
        </button>

        {/* 年月選択 */}
        <div className="flex items-center gap-1">
          <select
            value={currentYear}
            onChange={(e) => changeYear(Number(e.target.value))}
            className="w-16 appearance-none rounded border-gray-300 bg-white py-0 pl-1.5 pr-3.5 text-left text-xs font-semibold leading-5 text-gray-700 focus:border-blue-500 focus:outline-none"
            style={{
              backgroundPosition: 'right 3px center',
              backgroundSize: '9px',
              borderWidth: '0.5px',
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {formatYear(year)}
              </option>
            ))}
          </select>
          <select
            value={currentMonth}
            onChange={(e) => changeMonth(Number(e.target.value))}
            className="w-[3.25rem] appearance-none rounded border-gray-300 bg-white py-0 pl-1.5 pr-3.5 text-left text-xs font-semibold leading-5 text-gray-700 focus:border-blue-500 focus:outline-none"
            style={{
              backgroundPosition: 'right 3px center',
              backgroundSize: '9px',
              borderWidth: '0.5px',
            }}
          >
            {months.map((month, index) => (
              <option key={index} value={index}>
                {month}
              </option>
            ))}
          </select>
        </div>

        {/* 次月ボタン */}
        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className="rounded p-0.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t('次の月')}
        >
          <ChevronRightIcon className="size-3.5 text-gray-600" />
        </button>
      </div>
    );
  };

  return (
    <ReactDatePicker
      selected={selected}
      onChange={onChange}
      locale={locale}
      minDate={minDate || undefined}
      maxDate={maxDate || undefined}
      openToDate={selected || maxDate || new Date()}
      renderCustomHeader={renderCustomHeader}
      className="rounded-none rounded-tr-xl border-2 border-rw-light-gray text-black shadow-none"
      customInput={<BaseInput className="!px-2 shadow-none" />}
      placeholderText="y/m/d"
      dateFormat="yyyy/MM/dd"
      todayButton="Today"
      popperPlacement="bottom-start"
      popperClassName="z-[9999]"
    />
  );
};

type DateRangePickerProps = {
  label?: string;
  locale?: string;
  note?: string;
  selectedStart?: Date | null | undefined;
  selectedEnd?: Date | null | undefined;
  minDate?: Date | null;
  onStartChange?: (
    date: Date | null,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void;
  onEndChange?: (
    date: Date | null,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void;
};

export const DateRangePicker = ({
  label,
  locale,
  note,
  selectedStart,
  selectedEnd,
  minDate = null,
  onStartChange,
  onEndChange,
}: DateRangePickerProps) => {
  const { t } = useTranslation();

  // 年選択の範囲を明確にするため、min/maxの基準日を算出
  const currentDate = new Date();
  const defaultMinDate = new Date();
  defaultMinDate.setFullYear(currentDate.getFullYear() - 10);

  // 世界で最も進んでいるタイムゾーン（UTC+14）の日付を基準にする
  // これにより、どこかで明日になっている場合は明日まで選択可能
  const maxDate = new Date();
  maxDate.setTime(currentDate.getTime() + 14 * 60 * 60 * 1000); // UTC+14時間
  maxDate.setHours(23, 59, 59, 999); // その日の終わりまで

  const effectiveMinDate = minDate ?? defaultMinDate;
  const endMinDate = (() => {
    if (!selectedStart) return effectiveMinDate;
    return selectedStart > effectiveMinDate ? selectedStart : effectiveMinDate;
  })();

  return (
    <div>
      {label ? (
        <div className="flex items-center gap-1 text-xs font-medium">
          <CalendarIcon className="size-4 text-rw-gray" aria-hidden="true" />
          <span>{label}</span>
        </div>
      ) : null}
      {note ? <p className="mt-1 text-xs text-rw-gray">{note}</p> : null}
      <div className="mt-1 flex items-center gap-1">
        <div className="relative flex items-center">
          <ReactDatePickerBase
            selected={selectedStart ?? null}
            onChange={onStartChange}
            locale={locale}
            minDate={effectiveMinDate}
            maxDate={maxDate}
          />
          {selectedStart && (
            <button
              type="button"
              onClick={() => onStartChange?.(null)}
              className="absolute right-1 rounded p-0.5 hover:bg-gray-100"
              aria-label={t('開始日をクリア')}
            >
              <XMarkIcon className="size-3 text-gray-500" />
            </button>
          )}
        </div>
        <ArrowRightIcon className="size-4 text-gray-400" />
        <div className="relative flex items-center">
          <ReactDatePickerBase
            selected={selectedEnd ?? null}
            minDate={endMinDate}
            onChange={onEndChange}
            locale={locale}
            maxDate={maxDate}
          />
          {selectedEnd && (
            <button
              type="button"
              onClick={() => onEndChange?.(null)}
              className="absolute right-1 rounded p-0.5 hover:bg-gray-100"
              aria-label={t('終了日をクリア')}
            >
              <XMarkIcon className="size-3 text-gray-500" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
